import type { FC } from 'react';
import React, { useCallback, memo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';

import { ShortcutListener } from './ShortcutListener.tsx';

type AppNavigationShortcutProps = {
	accelerator: string | string[];
	deprecatedAccelerator?: string | string[];
	listener: (e: KeyboardEvent) => boolean | void;
};

export const AppNavigationShortcut: FC<AppNavigationShortcutProps> = memo(
	({ accelerator, listener, deprecatedAccelerator }) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const isNav4Enabled = useIsNav4Enabled();

		const listenerWithAnalytics = useCallback(
			(e: any) => {
				const key =
					typeof accelerator === 'string'
						? accelerator
						: accelerator.find((shortcut) => shortcut.endsWith(e.key)) || accelerator[0];
				if (listener(e) !== false) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'pressed',
							actionSubject: 'keyboardShortcut',
							source: 'globalNavigation',
							attributes: {
								key,
								navVersion: isNav4Enabled ? '4' : '3',
								navdexPointType: START_TOUCH,
							},
						},
					}).fire();
				}
			},
			[accelerator, listener, createAnalyticsEvent, isNav4Enabled],
		);

		return (
			<ShortcutListener
				accelerator={accelerator}
				deprecatedAccelerator={deprecatedAccelerator}
				listener={listenerWithAnalytics}
			/>
		);
	},
);
